import Axios from 'axios'
import csc, { ShopFront } from '@csc/csc-sdk'
import {
  DTP_NUMBER_FIELD,
  CHECKOUT_VERSION_FIELD,
  STORE_CREDITS_APPLIED_FIELD,
  CSC_SHOPFRONT_API,
  CSC_PAYMENTS_HOST,
  CSC_PAYMENTS_GATEWAY,
  CSC_PAYMENTS_SHOP,
  OAUTH_API,
} from '@/services/Configuration'

import {
  areAllShipmentsAssigned,
} from '@/services/Helpers'

import {
  standardizeAddressForBigC,
  standardizeOrder,
  standardizeCardFromCscPayments,
  MUTATIVELY_appendArrivalInformationToOrderCartAfterStandardizing,
} from '@/services/Standardizers'

import {
  isValidAddress,
  purchaseEventGA,
  roundToNDigits,
} from '@/helpers'

import { cardNumberToCardType } from '@csc/dls';

import { getSavedInstrumentsCredentials, clearSavedGuestToken } from './cscPaymentsServices'

import { log, error } from '@/services/Log'

import { getCartFromResponse } from './cartServices'
import { recoverShippingOptions } from '@/services/FramedCheckout/shipmentServices'
import { goToConfirmation } from '@/services/FramedCheckout/navigationServices'
import { heapPurchaseTracking } from './thridPartyServices'
//---------------------------------------------

const customFieldCheckoutVersion = {
  fieldId: CHECKOUT_VERSION_FIELD,
  fieldValue: '1',
}

const customFieldDTP = {
  fieldId: DTP_NUMBER_FIELD,
  fieldValue: '1',
}
export const updateBillingAddress = async ({
  order, billingAddress, customer, isPaypalOrder = false,
}) => {
  log('updateBillingAddress START', { order, billingAddress })
  let success = true

  const orderTotal = order?.subtotal + order?.shippingCostTotal + order?.taxTotal
  const storeCreditsAvailable = customer?.storeCreditAmounts?.find(({ amount }) => !!amount)?.amount
  const amountToRedeem = roundToNDigits(
    (
      isPaypalOrder
        ? Math.min(orderTotal, storeCreditsAvailable)
        : order?.storeCreditDiscount
    ) || 0,
  )

  const customFieldStoreCreditsApplied = {
    fieldId: STORE_CREDITS_APPLIED_FIELD,
    fieldValue: `${amountToRedeem}`,
  }

  const shipmentsCustomFields = (
    order?.shipments
      ?.filter(({ shippingAddress }) => shippingAddress?.customFields?.length)
      ?.map(({ shippingAddress }) => shippingAddress?.customFields)
      ?.reduce((acc = [], fields) => [...acc, ...fields], [])
    || []
  )
  const isDTP = customer?.customerGroupId === 3
  const DtpFields = isDTP ? [customFieldDTP] : []

  if ('form_fields' in billingAddress) { // signed in address
    billingAddress.form_fields = [
      ...(billingAddress?.form_fields || []),
      ...shipmentsCustomFields,
      ...DtpFields,
      customFieldCheckoutVersion,
      customFieldStoreCreditsApplied,
    ]
  } else {
    billingAddress.customFields = [
      ...(billingAddress?.customFields || []),
      ...shipmentsCustomFields,
      ...DtpFields,
      customFieldCheckoutVersion,
      customFieldStoreCreditsApplied,
    ]
  }

  const isUpdateOperation = (
    !!order?.billingAddress
    && !!order?.billingAddress?.id
    && isValidAddress(order?.billingAddress)
  )

  // Updates an existing billing address on Checkout.  --  https://developer.bigcommerce.com/api-reference/storefront/checkouts/checkout-billing-address/checkoutsbillingaddressbycheckoutidpost
  // Adds a billing address to an existing Checkout.   --  https://developer.bigcommerce.com/api-reference/storefront/checkouts/checkout-billing-address/checkoutsbillingaddressbycheckoutidpost
  const billingAddressEndpoint = isUpdateOperation
    ? `/api/storefront/checkouts/${order?.cart?.id}/billing-address/${order?.billingAddress?.id}?include=consignments.availableShippingOptions`
    : `/api/storefront/checkouts/${order?.cart?.id}/billing-address?include=consignments.availableShippingOptions`

  const httpMethod = isUpdateOperation ? Axios.put : Axios.post

  try {
    const response = (
      await httpMethod(billingAddressEndpoint, standardizeAddressForBigC(billingAddress))
    )
    success = success && response.status === 200
    const cart = getCartFromResponse(response?.data?.cart, order)
    const newOrder = standardizeOrder(response?.data, cart)
    await MUTATIVELY_appendArrivalInformationToOrderCartAfterStandardizing({ order: newOrder })
    const returnValue = {
      success,
      newOrder,
      areAllShipmentsAssigned: areAllShipmentsAssigned(newOrder),
      isUpdateOperation,
      billingAddressEndpoint,
    }
    if (!returnValue.areAllShipmentsAssigned) {
      error('updateBillingAddress ERROR areAllShipmentsAssigned = FALSE')
      log('updateBillingAddress => recoverShippingOptions', { params: { order, newOrder }, ...returnValue })
      return await recoverShippingOptions({ order, newOrder })
    }
    log('updateBillingAddress END', { ...returnValue })
    return returnValue
  } catch (err) {
    error(`updateBillingAddress FAILED ${err}`)
    return { success: false }
  }
}

const getBigCommerceSingleton = async () => {
  const { createCheckoutService } = await import('@bigcommerce/checkout-sdk')
  return createCheckoutService()
}

export const checkoutWithPaypal = async ({ checkoutId, billingAddress }) => {
  log('checkoutWithPaypal Start', { checkoutId, billingAddress })
  // TODO: this function needs more comments regarding what each step is doing and why they are necessary
  const checkoutClient = await getBigCommerceSingleton()
  /* const state = */ await checkoutClient.loadCheckout(checkoutId)

  if (billingAddress) {
    billingAddress = standardizeAddressForBigC(billingAddress)
    if ('form_fields' in billingAddress) { // signed in address
      billingAddress.form_fields = [
        ...(billingAddress?.form_fields || []),
        customFieldCheckoutVersion,
      ]
    } else {
      billingAddress.customFields = [
        ...(billingAddress?.customFields || []),
        customFieldCheckoutVersion,
      ]
    }
    try {
      await checkoutClient.updateBillingAddress(billingAddress)
    } catch (err) {
      error('Failed to update paypal Billing address', { err })
    }
  }
  /* const paymentMethods = */ (await checkoutClient.loadPaymentMethods()).data.getPaymentMethods()

  // Initiate PayPal transaction
  /* const initializedPayment = */ checkoutClient.initializePayment({ methodId: 'paypalexpress' })
  const paypalPayment = await checkoutClient.submitOrder({ payment: { methodId: 'paypalexpress' } })
  log('Paypal payment finalized without redirecting the customer')
  const order = await paypalPayment.data.getOrder()
  const { orderId, status } = order
  log(`Paypal returned ${JSON.stringify({ orderId, status })}`)
  if (!Number.isNaN(orderId) && orderId > 0 && status === 'AWAITING_FULFILLMENT') {
    try {
      const orderConfirmationResponse = await Axios.post(`${OAUTH_API}/sf/order/${orderId}/${billingAddress?.zip || billingAddress?.postalCode}`)
      log({ orderConfirmationResponse })
    } catch (err) {
      error(`Failed to send the order confirmation email ${err}`)
    }
    try {
      await heapPurchaseTracking({ order, lineItems: order?.cart?.lineItems || [] })
    } catch {
      //fail silently
    }
    await goToConfirmation(orderId)
  }
  return { success: true }
}

export const createPaymentToken = async ({
  ccNumber, expiryMonth: expirationMonth, expiryYear, cvv, billingAddress, customerEmail
}) => {
  log('CreatePaymentToken Start', { ccNumber, expiryMonth: expirationMonth, expiryYear, cvv, billingAddress, customerEmail })

  const {
    street1,
    city,
    zip,
    stateAcronym,
    countryCode = 'US',
    firstName,
    lastName,
    email: billingEmail,
    phone,
  } = billingAddress

  const email = billingEmail || customerEmail //clean up at invocation source in sf-checkout
  const credentials = await getSavedInstrumentsCredentials()
  const cardType = cardNumberToCardType(ccNumber).cscPaymentsType
  const number = ccNumber.replace(/\ /g, '')
  const newCard = {
    identity: credentials.identitySourceId,
    gatewayName: CSC_PAYMENTS_GATEWAY,
    mediaType: "card",
    pan: {
      number,
      securityCode: cvv
    },
    meta: {
      last4: number.slice(number.length - 4, number.length),
      billTo: {
        street1,
        street2: undefined, //why?
        postalCode: zip,
        city,
        country: countryCode,
        state: stateAcronym,
        firstName,
        lastName,
        email,
        phone
      },
      expiration: {
        year: `20${expiryYear}`,
        month: expirationMonth
      },
      type: cardType,
    }
  }
  try {
    log('createPaymentMethod: ', { newCard })
    const res = await csc.Payments({ credentials, host: CSC_PAYMENTS_HOST }).createPaymentMethod(newCard)
    return res?.paymentMethodId
  } catch (e) {
    log(e)
  }
  log('CreatePaymentToken End')
}

// move this to its own helper file, get from consignments later
const lineItemsTransformer = ({ taxTotal, lineItems, cartDiscount }) => lineItems.map(lineItem => {
  const itemDiscount = lineItem?.discounts?.reduce(
    (totalDiscount, currDiscount) =>
      currDiscount.discountedAmount + totalDiscount, 0)
  return {
    id: lineItem.id,
    quantity: lineItem.quantity,
    sku: lineItem.sku,
    name: lineItem.originalName,
    productId: String(lineItem.productId),
    unitPrice: lineItem.salePrice - itemDiscount,
    unitTax: 0, // figure this out later
    itemDiscount: itemDiscount ?? 0,
    cartDiscount,
  }
})

export const completePayment = async ({
  cardToken,
  methodToken,
  cartId,
  checkoutId = cartId,
  amountToBill,
  taxTotal,
  couponOrPromoCode = '',
  customer,
  shippingAddress,
  lineItems,
  userType = 'guest',
  billingAddress,
  cartDiscount,
  order,
}) => {
  log('completePayment Start')

  const {
    street1,
    street2 = undefined,
    city,
    zip,
    stateAcronym,
    countryCode = 'US',
    firstName,
    lastName,
    phone,
  } = shippingAddress
  const credentials = await getSavedInstrumentsCredentials()
  const deviceFingerprintId = window?.localStorage?.getItem('dfSessionId') ?? ''

  const paymentsBody = {
    shopName: CSC_PAYMENTS_SHOP,
    identity: credentials.identitySourceId,
    checkoutId,
    paymentMethodId: cardToken || methodToken,
    totalAmount: amountToBill,
    //anti-fraud DM below this point
    shippingAddress: {
      street1,
      street2,
      postalCode: zip,
      city,
      country: countryCode,
      state: stateAcronym,
      firstName,
      lastName,
      phone,
      email: customer?.email
    },
    lineItems: lineItemsTransformer({ taxTotal, lineItems, cartDiscount }),
    orderContext: {
      registeredOrGuest: userType,
      channel: 'ECOM',
      couponOrPromoCode
    },
    deviceFingerprintId
  }
  log({ paymentsBody })

  const { success, orderId, paymentId } = await ShopFront({
    credentials,
    host: CSC_SHOPFRONT_API,
    paymentsHost: CSC_PAYMENTS_HOST
  }).payForOrder(paymentsBody)

  clearSavedGuestToken() //whether it succeeds or fails, you will want a new guest token

  try {
    // Send orderConfirmation Email
    await Axios.post(`${OAUTH_API}/sf/order/${orderId}/${billingAddress?.zip}`)
  } catch (err) {
    error(`Failed to send the order confirmation email ${err}`)
  }
  try { // Send conversion data to GA
    log(`Send purchaseEventGA ${JSON.stringify({ orderId, order })}`)
    const { success } = await purchaseEventGA({
      orderId,
      billingAddress: order.billingAddress,
      coupons: order?.coupons || [],
      totalDiscount: order.totalDiscount,
      lineItems: order.cart.lineItems,
      shippingCost: order.shippingCostTotal,
      email: order.cart.email,
      taxTotal: order.taxTotal,
      grandTotal: order.grandTotal,
      cartTotal: order.cart.total,
    })
    log(`GA Purchase success = ${success}`)
  } catch (err) {
    error(`Failed to send conversion to GA ${err}`)
  }
  log('completePayment End', { success, orderId })
  return { success, orderId }
}

// Payment
export const getPaymentMethods = async () => {
  const credentials = await getSavedInstrumentsCredentials()
  const CSCPaymentPortal = csc.Payments({ credentials, host: CSC_PAYMENTS_HOST })
  const savedInstruments = await CSCPaymentPortal.listPaymentMethods({ identity: credentials.identitySourceId })
  // log('CSC raw savedInstruments', { savedInstruments })
  const savedInstrumentsWithStandardizedAddress = savedInstruments.map(standardizeCardFromCscPayments)
  // log('getPaymentMethods END', { instruments: savedInstrumentsWithStandardizedAddress })
  return savedInstrumentsWithStandardizedAddress
}



/**
 * Create a payment method associated with the logged in user (identified by their cookies)
 * Very hacky, reverse-engineered spiffy spring to make this work. Nothing is documented officially
 *
 * @param {Object} paymentParam
 * @param {Object} paymentParam.instrument
 * @param {Object} paymentParam.instrument.creditCardInfo - the credit card information and its billing address
 * @param {string} paymentParam.instrument.creditCardInfo.ccNumber
 * @param {string} paymentParam.instrument.creditCardInfo.expiry - "MM/YY" format
 * @param {string} paymentParam.instrument.creditCardInfo.cvv
 * @param {Object} paymentParam.instrument.billingAddress
 * @param {string} paymentParam.instrument.billingAddress.street1
 * @param {string} paymentParam.instrument.billingAddress.city
 * @param {string} paymentParam.instrument.billingAddress.zip
 * @param {string} paymentParam.instrument.billingAddress.stateAcronym
 * @param {string} paymentParam.instrument.billingAddress.countryCode
 * @param {string} paymentParam.instrument.billingAddress.company
 * @param {string} paymentParam.instrument.billingAddress.firstName
 * @param {string} paymentParam.instrument.billingAddress.lastName
 * @param {string} paymentParam.instrument.billingAddress.email
 * @param {string} paymentParam.instrument.billingAddress.phone
 * @returns {Object} - saved instrument
 */
export const createPaymentMethod = async ({ instrument }) => {
  const {
    creditCardInfo: {
      ccNumber,
      expiry,
      cvv,
    },
    billingAddress: {
      street1,
      city,
      zip,
      stateAcronym,
      countryCode = 'US',
      firstName,
      lastName,
      email,
      phone,
    },
  } = instrument

  const credentials = await getSavedInstrumentsCredentials()
  const cardType = cardNumberToCardType(ccNumber).cscPaymentsType
  const newCard = {
    identity: credentials.identitySourceId,
    gatewayName: CSC_PAYMENTS_GATEWAY,
    mediaType: "card",
    pan: {
      number: ccNumber.replace(/\ /g, ''),
      securityCode: cvv
    },
    meta: {
      last4: ccNumber.slice(ccNumber.length - 4, ccNumber.length),
      billTo: {
        street1,
        street2: undefined,
        postalCode: zip,
        city,
        country: countryCode,
        state: stateAcronym,
        firstName,
        lastName,
        email,
        phone
      },
      expiration: {
        year: `20${expiry.split('/')[1]}`,
        month: expiry.split('/')[0], //parse later
      },
      type: cardType,
    }
  }

  try {
    const res = await csc.Payments({ credentials, host: CSC_PAYMENTS_HOST }).createPaymentMethod(newCard)
    log(res)

    return {
      success: true,
      savedInstruments: await getPaymentMethods(),
    }
  } catch (e) {
    error(e)
  }
  return { success: false }
}

export const setPaymentMethod = async () => 'ok'
// Confirmation
export const payForOrder = async () => 'ok'
/**
 * Delete a payment method
 * @param {object} card
 */
export const deletePaymentMethod = async ({ cardDataFromCSCPayments }) => {
  try {
    // Send a request to parky to delete the instrument
    const credentials = await getSavedInstrumentsCredentials()
    const res = await csc.Payments({ credentials, host: CSC_PAYMENTS_HOST }).deletePaymentMethod(cardDataFromCSCPayments)
    return {
      success: true,
    }
  } catch (err) {
    error('Error in deletePaymentMethod', err)
    return {
      success: false,
    }
  }
}
